import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, functions } from '../firebase-config';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  REPAIR_CATEGORIES,
  TRADE_META,
  JOIN_ONBOARDING_STEPS,
  YEARS_OPTIONS,
  PRO_DEPOSIT_AMOUNT,
  PRO_LANDING_INTEREST_EMAIL_KEY,
  OWNER_PRO_INVITE_TOKEN_KEY,
} from '../utils/professionalOnboardingConstants';
import AddressAutocomplete from './AddressAutocomplete';
import ServiceAreaMap from './ServiceAreaMap';

/**
 * Public, pre-signup multi-step wizard for service professionals.
 * Runs BEFORE email/password. At the end we create a Firebase Auth account,
 * write `ProfessionalsData` with Firestore `setDoc`, then redirect to `/professional`.
 */

const PRO_LANDING_SIGNUP_NOTES_KEY = 'proLandingSignupNotes';

function ProfessionalJoinWizard() {
  const navigate = useNavigate();

  // Step & qualification state
  const [step, setStep] = useState(1);
  const [trades, setTrades] = useState([]);
  const [hasLicense, setHasLicense] = useState(null);
  const [hasInsurance, setHasInsurance] = useState(null);
  const [yearsExperience, setYearsExperience] = useState('');
  const [specialties, setSpecialties] = useState(() => {
    try {
      const raw = sessionStorage.getItem(PRO_LANDING_SIGNUP_NOTES_KEY);
      return raw || '';
    } catch {
      return '';
    }
  });
  const [hasReferences, setHasReferences] = useState(null);
  const [referencesLink, setReferencesLink] = useState('');

  // Business profile
  const [businessName, setBusinessName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [serviceRadius, setServiceRadius] = useState('20');
  const [serviceFee, setServiceFee] = useState('');

  // Account creation
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(() => {
    try {
      return sessionStorage.getItem(PRO_LANDING_INTEREST_EMAIL_KEY)?.trim() || '';
    } catch {
      return '';
    }
  });
  const [password, setPassword] = useState('');

  // Deposit
  const [depositStatus, setDepositStatus] = useState('idle'); // idle | loading | paid | cancelled
  const [depositError, setDepositError] = useState('');

  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Prefill from query params + handle Stripe return (anon deposit). Run once on mount only —
  // do not depend on `email` or this effect will run every keystroke and can fight the URL sync.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefillEmail = params.get('email')?.trim();
    if (prefillEmail) {
      try {
        sessionStorage.setItem(PRO_LANDING_INTEREST_EMAIL_KEY, prefillEmail);
      } catch {
        /* ignore */
      }
      setEmail((prev) => (prev ? prev : prefillEmail));
    }

    const ownerProInvite = params.get('proInvite')?.trim();
    if (ownerProInvite) {
      try {
        sessionStorage.setItem(OWNER_PRO_INVITE_TOKEN_KEY, ownerProInvite);
      } catch {
        /* ignore */
      }
    }

    const sid = params.get('deposit_session_id')?.trim();
    const cancelled = params.get('deposit_cancelled');
    if (cancelled) {
      setDepositStatus('cancelled');
      setDepositError('Checkout was cancelled. You can pay now or skip and pay later to unlock jobs.');
    }
    if (sid) {
      // We can’t verify without auth yet; final submit will verify server-side.
      setDepositStatus('paid');
    }

    if (prefillEmail || ownerProInvite || sid || cancelled) {
      const url = new URL(window.location.href);
      url.searchParams.delete('email');
      url.searchParams.delete('proInvite');
      url.searchParams.delete('deposit_session_id');
      url.searchParams.delete('deposit_cancelled');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const licensedTrades = useMemo(
    () => trades.filter((t) => TRADE_META[t]?.requiresLicense),
    [trades]
  );
  const isHandyman = trades.includes('General Maintenance (Handyman)');
  const generalTrades = useMemo(
    () => trades.filter((t) => TRADE_META[t]?.tradeType === 'general'),
    [trades]
  );

  const canContinueFromStep1 = trades.length > 0;

  const handleNext = () => {
    if (step === 1) {
      if (!canContinueFromStep1) return;
      setStep(2);
      return;
    }
    if (step === 2) {
      if (licensedTrades.length > 0) {
        if (hasLicense !== true || hasInsurance !== true) {
          // Soft gate: keep them here with explanation instead of advancing.
          return;
        }
      }
      if (isHandyman && !yearsExperience) return;
      setStep(3);
      return;
    }
    if (step === 3) {
      if (!businessName.trim() || !serviceArea.trim() || !serviceFee) return;
      setStep(4);
      return;
    }
    if (step === 4) {
      setStep(5);
      return;
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleDepositCheckout = async () => {
    setDepositStatus('loading');
    setDepositError('');
    try {
      const createCheckout = httpsCallable(functions, 'createProOnboardingCheckoutAnonymous');
      const result = await createCheckout({});
      const url = result.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      setDepositError('Could not start checkout. Please try again.');
      setDepositStatus('idle');
    } catch (err) {
      console.error('anon deposit error', err);
      setDepositError(
        err?.message || 'Could not start checkout. Please try again or email hello@tenantdispatch.com.'
      );
      setDepositStatus('idle');
    }
  };

  const handleSubmitAccount = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail || !password) {
        throw new Error('Email and password are required.');
      }
      const cred = await createUserWithEmailAndPassword(auth, trimmedEmail, password);

      // Write full profile directly to Firestore — same pattern as owner/realtor/tenant signup.
      // No Cloud Function callable needed; deposit linking (if any) happens from the dashboard.
      await setDoc(
        doc(db, 'ProfessionalsData', cred.user.uid),
        {
          email: trimmedEmail,
          role: 'professional',
          fullName: fullName || null,
          companyName: businessName,
          businessName,
          phoneNumber: phoneNumber || null,
          categories: trades,
          selectedTrades: trades,
          serviceArea,
          serviceRadius,
          serviceFee,
          qualifications: {
            hasLicense,
            hasInsurance,
            yearsExperience,
            specialties,
            hasReferences,
            referencesLink,
          },
          depositPaid: false,
          setupComplete: true,
          onboardingStep: 5,
          joinedVia: 'public_join_wizard',
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        { merge: true }
      );

      try {
        sessionStorage.removeItem(PRO_LANDING_INTEREST_EMAIL_KEY);
      } catch {
        /* ignore */
      }
      navigate('/professional');
    } catch (err) {
      console.error('Professional join submit error', err);
      setSubmitError(err?.message || 'Could not create your account. Please try again.');
      setSubmitting(false);
    }
  };

  // Plain render helper — not a nested component. A nested `const StepBody = () => …` would be a
  // new component type every render and remount inputs on each keystroke (lost focus / “refresh”).
  const renderStepBody = () => {
    if (step === 1) {
      return (
        <div className="pro-onboarding-hero">
          <h2 className="pro-onboarding-hero-title">What kind of work do you do?</h2>
          <p className="pro-onboarding-hero-subtitle">
            Pick every trade that feels like a real part of your week. We use this to match you with maintenance
            requests.
          </p>
          <div className="pro-onboarding-trade-grid">
            {REPAIR_CATEGORIES.map((cat) => {
              const meta = TRADE_META[cat] || { icon: '📦', requiresLicense: false, tradeType: 'general' };
              const selected = trades.includes(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  className={`pro-onboarding-trade-card ${selected ? 'is-selected' : ''}`}
                  onClick={() => {
                    setTrades((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
                  }}
                >
                  <span className="pro-onboarding-trade-icon">{meta.icon}</span>
                  <span className="pro-onboarding-trade-name">{cat}</span>
                  {meta.requiresLicense && <span className="pro-onboarding-trade-badge">License required</span>}
                  {selected && <span className="pro-onboarding-trade-check">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="pro-onboarding-actions">
            <button type="button" className="btn-primary" disabled={!canContinueFromStep1} onClick={handleNext}>
              Continue
            </button>
          </div>
        </div>
      );
    }

    if (step === 2) {
      const showGate = licensedTrades.length > 0 && (hasLicense !== true || hasInsurance !== true);
      return (
        <div className="pro-onboarding-hero">
          <h2 className="pro-onboarding-hero-title">Credentials & coverage</h2>
          <p className="pro-onboarding-hero-subtitle">
            We keep things simple: answer honestly so owners know who is coming to the property.
          </p>

          {licensedTrades.length > 0 && (
            <div className="pro-onboarding-qual-section">
              <h3 className="pro-onboarding-qual-heading">Licensed trades: {licensedTrades.join(', ')}</h3>
              <div className="pro-onboarding-qual-row">
                <label className="pro-onboarding-qual-label">Do you hold an active license?</label>
                <div className="pro-onboarding-toggle-group">
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasLicense === true ? 'is-active' : ''}`}
                    onClick={() => setHasLicense(true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasLicense === false ? 'is-active' : ''}`}
                    onClick={() => setHasLicense(false)}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="pro-onboarding-qual-row">
                <label className="pro-onboarding-qual-label">Do you carry general liability insurance?</label>
                <div className="pro-onboarding-toggle-group">
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasInsurance === true ? 'is-active' : ''}`}
                    onClick={() => setHasInsurance(true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasInsurance === false ? 'is-active' : ''}`}
                    onClick={() => setHasInsurance(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          )}

          {isHandyman && (
            <div className="pro-onboarding-qual-section">
              <h3 className="pro-onboarding-qual-heading">General Maintenance / Handyman</h3>
              <div className="pro-onboarding-qual-row">
                <label className="pro-onboarding-qual-label">Years of experience</label>
                <select
                  className="input-field"
                  value={yearsExperience}
                  onChange={(e) => setYearsExperience(e.target.value)}
                >
                  <option value="">Select...</option>
                  {YEARS_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pro-onboarding-qual-row">
                <label className="pro-onboarding-qual-label">Top specialties</label>
                <input
                  type="text"
                  className="input-field"
                  value={specialties}
                  onChange={(e) => setSpecialties(e.target.value)}
                  placeholder="e.g. drywall patching, furniture assembly, minor plumbing"
                />
              </div>
              <div className="pro-onboarding-qual-row">
                <label className="pro-onboarding-qual-label">Do you have references or a portfolio?</label>
                <div className="pro-onboarding-toggle-group">
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasReferences === true ? 'is-active' : ''}`}
                    onClick={() => setHasReferences(true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasReferences === false ? 'is-active' : ''}`}
                    onClick={() => setHasReferences(false)}
                  >
                    No
                  </button>
                </div>
                {hasReferences && (
                  <input
                    type="url"
                    className="input-field"
                    value={referencesLink}
                    onChange={(e) => setReferencesLink(e.target.value)}
                    placeholder="Link to portfolio or references (optional)"
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            </div>
          )}

          {generalTrades.length > 0 && !isHandyman && licensedTrades.length === 0 && (
            <div className="pro-onboarding-qual-section">
              <h3 className="pro-onboarding-qual-heading">Your trades: {generalTrades.join(', ')}</h3>
              <div className="pro-onboarding-qual-row">
                <label className="pro-onboarding-qual-label">
                  Do you carry general liability insurance? (recommended)
                </label>
                <div className="pro-onboarding-toggle-group">
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasInsurance === true ? 'is-active' : ''}`}
                    onClick={() => setHasInsurance(true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`pro-onboarding-toggle ${hasInsurance === false ? 'is-active' : ''}`}
                    onClick={() => setHasInsurance(false)}
                  >
                    No
                  </button>
                </div>
              </div>
              <div className="pro-onboarding-qual-row">
                <label className="pro-onboarding-qual-label">Years of experience</label>
                <select
                  className="input-field"
                  value={yearsExperience}
                  onChange={(e) => setYearsExperience(e.target.value)}
                >
                  <option value="">Select...</option>
                  {YEARS_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {showGate && (
            <div className="pro-onboarding-gate">
              <div className="pro-onboarding-gate-icon">📋</div>
              <h2 className="pro-onboarding-gate-title">We will keep you posted</h2>
              <p className="pro-onboarding-gate-text">
                For some trades we require an active license and insurance before we can show jobs. You can still finish
                creating your account—we will reach out if our requirements change for your trade or area.
              </p>
            </div>
          )}

          <div className="pro-onboarding-actions">
            <button type="button" className="pro-onboarding-back" onClick={handleBack}>
              Back
            </button>
            <button type="button" className="btn-primary" onClick={handleNext}>
              Continue
            </button>
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="pro-onboarding-hero">
          <h2 className="pro-onboarding-hero-title">Where do you want work?</h2>
          <p className="pro-onboarding-hero-subtitle">
            Set your turf now; you can adjust service area and pricing from your profile later.
          </p>
          <div className="pro-onboarding-form">
            <div className="form-group">
              <label className="pro-onboarding-field-label">Business name</label>
              <input
                type="text"
                className="input-field"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Your business or company name"
              />
            </div>
            <div className="form-group">
              <label className="pro-onboarding-field-label">Phone number</label>
              <input
                type="tel"
                className="input-field"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="form-group">
              <label className="pro-onboarding-field-label">Service area (city / region)</label>
              <AddressAutocomplete
                value={serviceArea}
                onChange={(addressInput) => {
                  if (typeof addressInput === 'object' && addressInput?.address) {
                    setServiceArea(addressInput.address);
                  } else {
                    setServiceArea(addressInput);
                  }
                }}
                placeholder="Search and select city or address"
              />
            </div>
            <div className="form-group">
              <label className="pro-onboarding-field-label">Service radius (miles)</label>
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={serviceRadius}
                onChange={(e) => setServiceRadius(e.target.value)}
                style={{ width: '100%' }}
              />
              <span style={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>{serviceRadius} miles</span>
            </div>
            <ServiceAreaMap serviceArea={serviceArea} serviceRadius={parseInt(serviceRadius, 10) || 0} />
            <div className="form-group">
              <label className="pro-onboarding-field-label">Service fee per visit ($)</label>
              <input
                type="number"
                className="input-field"
                value={serviceFee}
                onChange={(e) => setServiceFee(e.target.value)}
                placeholder="e.g. 100"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div className="pro-onboarding-actions">
            <button type="button" className="pro-onboarding-back" onClick={handleBack}>
              Back
            </button>
            <button type="button" className="btn-primary" onClick={handleNext}>
              Continue
            </button>
          </div>
        </div>
      );
    }

    if (step === 4) {
      return (
        <div className="pro-onboarding-hero">
          <h2 className="pro-onboarding-hero-title">Unlock the job board</h2>
          <p className="pro-onboarding-hero-subtitle">
            A small, fully refundable ${PRO_DEPOSIT_AMOUNT} deposit covers identity and background checks. You get it
            back after your first completed paid job—or if we do not approve your application.
          </p>
          <div className="pro-onboarding-deposit-box">
            <div className="pro-onboarding-deposit-amount">${PRO_DEPOSIT_AMOUNT}.00</div>
            <p className="pro-onboarding-deposit-desc">
              We run checks once so owners and residents can feel confident inviting you into their buildings. You only
              pay this once for your account.
            </p>
            {depositError && <p className="pro-onboarding-deposit-error">{depositError}</p>}
            <button
              type="button"
              className="btn-primary"
              onClick={handleDepositCheckout}
              disabled={depositStatus === 'loading'}
              style={{ width: '100%', marginTop: 16 }}
            >
              {depositStatus === 'loading' ? 'Redirecting to checkout…' : `Pay $${PRO_DEPOSIT_AMOUNT} refundable deposit`}
            </button>
            <button
              type="button"
              className="pro-onboarding-skip"
              onClick={() => {
                setDepositStatus('cancelled');
                setStep(5);
              }}
            >
              Skip for now — you can pay later (jobs will stay blurred)
            </button>
          </div>
          <div className="pro-onboarding-actions">
            <button type="button" className="pro-onboarding-back" onClick={handleBack}>
              Back
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="pro-onboarding-hero">
        <h2 className="pro-onboarding-hero-title">Create your account</h2>
        <p className="pro-onboarding-hero-subtitle">
          One last step: we will save everything you just told us, then drop you into your dashboard.
        </p>
        <div className="pro-onboarding-form">
          <div className="form-group">
            <label className="pro-onboarding-field-label">Full name</label>
            <input
              type="text"
              className="input-field"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your name"
            />
          </div>
          <div className="form-group">
            <label className="pro-onboarding-field-label">Business email</label>
            <input
              type="email"
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          <div className="form-group">
            <label className="pro-onboarding-field-label">Password</label>
            <input
              type="password"
              className="input-field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
            />
          </div>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {depositStatus === 'paid'
              ? 'Your deposit is already paid. We will tie it to this account automatically.'
              : 'You can still see your account without paying. Jobs will stay blurred until your deposit is complete.'}
          </p>
        </div>
        {submitError && (
          <p className="pro-onboarding-deposit-error" style={{ marginTop: 16 }}>
            {submitError}
          </p>
        )}
        <div className="pro-onboarding-actions">
          <button type="button" className="pro-onboarding-back" onClick={handleBack} disabled={submitting}>
            Back
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmitAccount} disabled={submitting}>
            {submitting ? 'Creating your account…' : 'Create account'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="realtor-landing-page professional-landing-theme">
      <main className="realtor-landing-main">
        <section className="realtor-hero">
          <div className="realtor-hero-content">
            <div className="realtor-badge">🛠️ Step into real maintenance jobs</div>
            <h1 className="realtor-hero-title">
              Join the network{' '}
              <span className="realtor-hero-accent">before we open the board</span>
            </h1>
            <p className="realtor-hero-subtitle">
              Answer a few questions about your trade, turf, and coverage. We will show you how the workflow feels—
              then you decide if you want to finish sign-up and unlock jobs.
            </p>
          </div>
        </section>

        <section className="realtor-flow-section" style={{ marginTop: 0 }}>
          <div className="pro-onboarding-wizard">
            <div className="pro-onboarding-progress">
              {JOIN_ONBOARDING_STEPS.map((s) => (
                <div
                  key={s.num}
                  className={`pro-onboarding-progress-step ${step === s.num ? 'is-active' : ''} ${
                    step > s.num ? 'is-completed' : ''
                  }`}
                >
                  <div className="pro-onboarding-progress-circle">
                    {step > s.num ? '✓' : s.num}
                  </div>
                  <span className="pro-onboarding-progress-label">{s.label}</span>
                </div>
              ))}
            </div>

            {renderStepBody()}
          </div>
        </section>
      </main>
    </div>
  );
}

export default ProfessionalJoinWizard;

